import { createPortal } from 'react-dom'
import { modales } from '../contexts/modales'
import { useAuth } from '../AuthContext'
import { useActionsPortail } from '../contexts/navigation'
import { useCasquette } from '../contexts/casquette'
import { MVP } from '../mvp'
import { avecAlpha } from '../lib/couleurs'

/**
 * Selecteur de profil — bottom sheet unique (ref docs/manrina-app-complete.jsx).
 * Remplace le toggle Client/Producteur : une seule pastille (header) ouvre ce
 * modal, qui liste les casquettes de l'utilisateur. Style « liquid glass » vert
 * sombre, conforme au DESIGN-GUIDE (sheet glassDark + poignee + slideUp).
 */
function ProfileSheet({ open, onClose, hubMode, isProducer, user, onPick, onNavigate, onGoAccount }) {
  if (!open) return null

  const profiles = [
    { k: 'client', emoji: '🧑🏾', label: 'Client', desc: 'Commander, explorer le réseau', accent: 'rgb(var(--c-primary))' },
  ]
  if (isProducer) {
    profiles.push({ k: 'producer', emoji: '🌱', label: 'Producteur', desc: 'Mes offres, ventes et paiements', accent: 'rgb(var(--c-teal))' })
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end backdrop-animate"
      style={{ background: 'rgba(4,25,24,0.45)' }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full rounded-t-[30px] px-5 pt-3.5 pb-9 animate-sheet-up"
        style={{
          background: 'rgba(7,62,61,0.92)',
          border: '1px solid rgba(247,240,234,0.16)',
          boxShadow: '0 -12px 40px rgba(4,25,24,0.4)',
          color: 'rgb(var(--c-creme))',
          paddingBottom: 'max(2.25rem, env(safe-area-inset-bottom))',
        }}
      >
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(247,240,234,0.3)' }} />

        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-creme/55 px-1">
          Bonjou {user?.prenom || ''}
        </p>
        <p className="font-serif text-[22px] mb-3.5 px-1">Mes casquettes</p>

        <div className="flex flex-col gap-2.5">
          {profiles.map(p => {
            const active = hubMode === p.k
            return (
              <button
                key={p.k}
                onClick={() => { onPick(p.k); onClose() }}
                className="flex items-center gap-3.5 p-3.5 rounded-2xl text-left transition-all"
                style={{
                  background: active ? avecAlpha(p.accent, 0.15) : 'rgba(247,240,234,0.06)',
                  border: active ? `1.5px solid ${p.accent}` : '1px solid rgba(247,240,234,0.14)',
                }}
              >
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-[21px] flex-shrink-0"
                  style={{ background: avecAlpha(p.accent, 0.2) }}
                >
                  {p.emoji}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-bold">{p.label}</p>
                  <p className="text-[11.5px] text-creme/60 mt-0.5">{p.desc}</p>
                </div>
                {active && (
                  <span className="text-creme text-lg flex-shrink-0">✓</span>
                )}
              </button>
            )
          })}

          {/* MVP (S62) : « Devenir producteur » ne vit que dans la page Compte */}
          {!isProducer && MVP.DEVENIR_PRODUCTEUR_HEADER && (
            <button
              onClick={() => { onClose(); onNavigate('devenir-producteur') }}
              className="flex items-center gap-3.5 p-3.5 rounded-2xl text-left"
              style={{ background: 'rgba(247,240,234,0.06)', border: '1px dashed rgba(247,240,234,0.25)' }}
            >
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-[21px] flex-shrink-0" style={{ background: 'rgba(20,184,166,0.2)' }}>🌱</span>
              <div className="flex-1 min-w-0">
                <p className="text-[14.5px] font-bold">Devenir producteur</p>
                <p className="text-[11.5px] text-creme/60 mt-0.5">Vendre votre production sur le réseau</p>
              </div>
              <span className="text-creme/50 text-lg flex-shrink-0">›</span>
            </button>
          )}
        </div>

        <button
          onClick={() => { onClose(); onGoAccount() }}
          className="w-full mt-4 py-3 rounded-full text-[13px] font-bold"
          style={{ background: 'rgba(247,240,234,0.1)', color: 'rgb(var(--c-creme))' }}
        >
          Voir mon compte
        </button>
      </div>
    </div>,
    document.body,
  )
}

export default ProfileSheet

// Version CONNECTEE : lit auth + navigation dans les contextes AU RENDU
// (valeurs vivantes, pas de snapshot) — c'est elle qui permet a l'ouvreur
// de n'avoir AUCUNE dependance, donc d'etre appelable sans prop-drilling.
function FeuilleProfilConnectee({ onClose }) {
  const { user } = useAuth()
  const { hubMode, isProducer } = useCasquette()
  const { changerCasquette, navigate, setHubTab } = useActionsPortail()
  return (
    <ProfileSheet
      open
      onClose={onClose}
      hubMode={hubMode}
      isProducer={isProducer}
      user={user}
      onPick={changerCasquette}
      onNavigate={navigate}
      onGoAccount={() => setHubTab('account')}
    />
  )
}

/**
 * Ouvreur imperatif (ADR-066/067) — SANS dependances : chaque header/pastille
 * l'importe et l'appelle, plus aucune prop `onOpenProfile` a faire descendre.
 */
export function ouvrirProfil() {
  modales.ouvrir({
    id: 'profil',
    contenu: ({ fermer }) => <FeuilleProfilConnectee onClose={fermer} />,
  })
}
